import { useCallback, useState, useRef } from "react";
import type { KeyboardEvent } from "react";
import { useLocation, useNavigate } from "react-router";
import { useChatStore } from "../../stores/chat-store";
import { useChatUIStore } from "../../stores/chat-ui-store";
import { useChatSocketContext } from "../../contexts/useChatSocketContext";
import { MessageList } from "./MessageList";
import { ConnectionBanner } from "./ConnectionBanner";
import { Icon } from "../ui/Icon";
import { cn } from "../../lib/utils";
import ChatRoundDotsLinear from "~icons/solar/chat-round-dots-linear";
import MinimizeSquare from "~icons/solar/minimize-square-3-linear";
import MaximizeSquare from "~icons/solar/maximize-square-3-linear";
import ArrowRightUpLinear from "~icons/solar/arrow-right-up-linear";
import StopBold from "~icons/solar/stop-bold";

export function PersistentChatPanel() {
  const location = useLocation();
  const navigate = useNavigate();

  const messages = useChatStore((s) => s.messages);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const threadId = useChatStore((s) => s.threadId);

  const isPanelMinimized = useChatUIStore((s) => s.isPanelMinimized);
  const setPanelMinimized = useChatUIStore((s) => s.setPanelMinimized);
  const togglePanelMinimized = useChatUIStore((s) => s.togglePanelMinimized);

  const {
    connectionState,
    connected,
    sendMessage,
    resendLastMessage,
    stopGeneration,
    toolAnimation,
  } = useChatSocketContext();

  const [inputValue, setInputValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Hide entirely on the dedicated /chat route — full-screen ChatPage owns it there
  const isChatRoute = location.pathname === "/chat";

  const canSend =
    inputValue.trim().length > 0 && connected && !isStreaming;

  const handleSend = useCallback(() => {
    const text = inputValue.trim();
    if (!text || !connected || isStreaming) return;
    sendMessage(text);
    setInputValue("");
    textareaRef.current?.focus();
  }, [inputValue, connected, isStreaming, sendMessage]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (canSend) handleSend();
      }
    },
    [canSend, handleSend]
  );

  const handleExpandToFullView = useCallback(() => {
    const search = messages.length > 0 ? `?t=${threadId}` : "";
    navigate(`/chat${search}`);
  }, [navigate, messages.length, threadId]);

  if (isChatRoute) return null;

  // Minimized: floating bubble in the corner
  if (isPanelMinimized) {
    const hasMessages = messages.length > 0;
    return (
      <button
        type="button"
        onClick={() => setPanelMinimized(false)}
        className="fixed bottom-24 right-6 z-40 flex items-center gap-2 rounded-full border border-border-default bg-white px-4 py-3 shadow-lg transition-transform hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        aria-label="Open chat panel"
      >
        <Icon icon={ChatRoundDotsLinear} size="md" className="text-primary" />
        <span className="text-body-sm font-medium text-text-primary">Chat</span>
        {hasMessages && (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-gradient-to-r from-gradient-start to-gradient-end px-1.5 text-[10px] font-semibold leading-tight text-white">
            {messages.length > 99 ? "99+" : messages.length}
          </span>
        )}
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-24 right-6 z-40 flex w-[min(420px,calc(100vw-3rem))] flex-col overflow-hidden rounded-2xl border border-border-default bg-white shadow-2xl"
      style={{ height: "min(620px, calc(100vh - 8rem))" }}
      role="complementary"
      aria-label="Chat panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border-default bg-white px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon icon={ChatRoundDotsLinear} size="md" className="text-primary" />
          <span className="text-body-md font-semibold text-text-primary">
            Chat
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleExpandToFullView}
            className="flex size-8 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-grey-25 hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Open full chat view"
            title="Open full view"
          >
            <Icon icon={MaximizeSquare} size="sm" />
          </button>
          <button
            type="button"
            onClick={togglePanelMinimized}
            className="flex size-8 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-grey-25 hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Minimize chat panel"
            title="Minimize"
          >
            <Icon icon={MinimizeSquare} size="sm" />
          </button>
        </div>
      </div>

      <ConnectionBanner state={connectionState} />

      {/* Messages area */}
      <div className="flex min-h-0 flex-1 flex-col">
        {messages.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
            <Icon
              icon={ChatRoundDotsLinear}
              size="xl"
              className="mb-3 text-grey"
            />
            <p className="text-body-sm text-text-secondary">
              Start a conversation — your messages stay visible as you navigate.
            </p>
          </div>
        ) : (
          <MessageList
            messages={messages}
            onRetry={resendLastMessage}
            toolAnimation={toolAnimation}
          />
        )}
      </div>

      {/* Slim input — text-only. Use the full /chat view for files & @mentions. */}
      <div className="border-t border-border-default p-3">
        <div className="flex items-end gap-2 rounded-2xl border border-border-default bg-white p-2 focus-within:border-primary">
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={!connected}
            placeholder={connected ? "Type a message…" : "Connecting…"}
            rows={1}
            className="flex-1 resize-none bg-transparent px-2 py-2 text-body-sm font-medium text-text-primary outline-none placeholder:text-text-placeholder disabled:cursor-not-allowed"
            aria-label="Chat message"
          />
          {isStreaming ? (
            <button
              type="button"
              onClick={stopGeneration}
              className="flex size-9 shrink-0 items-center justify-center rounded-full bg-black-500 text-white transition-all hover:bg-black-500/85 active:scale-95"
              aria-label="Stop generating"
            >
              <Icon icon={StopBold} size="sm" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              className={cn(
                "flex size-9 shrink-0 items-center justify-center rounded-full transition-all",
                canSend
                  ? "bg-gradient-to-r from-gradient-start to-gradient-end text-white hover:brightness-110 active:scale-95"
                  : "border border-border-default bg-white text-text-placeholder"
              )}
              aria-label="Send message"
            >
              <Icon icon={ArrowRightUpLinear} size="sm" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
